import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Check, Snowflake } from "lucide-react";
import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";
import PairLedger from "@/components/landing/PairLedger";
import PairedDay from "@/components/landing/PairedDay";
import GhostFeed from "@/components/landing/GhostFeed";
import Pact from "@/components/landing/Pact";
import { CohortSection, NextCohortPill, useOpenCohorts } from "@/components/landing/NextCohort";
import { getBillingCatalog } from "@/lib/api";
import { formatMoney } from "@/lib/currency";

const EASE = [0.16, 1, 0.3, 1];

const fixes = [
  { title: "Smart matching", body: "Paired on goal, cadence, timezone and grit — not vibes." },
  { title: "Serious members only", body: "A small subscription filters out tourists. What’s left is signal." },
  { title: "Daily accountability", body: "Structured check-ins that make skipping a day feel loud." },
  { title: "Reputation system", body: "Show-ups compound. Ghosters get exposed. Trust is public." },
  { title: "Goal tracking", body: "From weekly targets to 90-day sprints — visible to your partner." },
  { title: "Streaks & analytics", body: "See the shape of your discipline, week over week." },
];

const steps = [
  { n: "I", title: "Declare a goal", body: "Tell us what you’re building, studying, or shipping. Add cadence and timezone." },
  { n: "II", title: "Get matched", body: "We pair you with 1–4 candidates based on grit, goal-fit and rhythm. You choose." },
  { n: "III", title: "Show up, daily", body: "Structured check-ins, streaks, and a reputation score that rewards consistency." },
];

const goals = [
  "Cracking JEE / GATE / GRE",
  "Shipping a startup",
  "Fitness & fat-loss",
  "Learning a new skill",
  "Writing a book",
  "Deep work every morning",
  "Reading 30 books this year",
  "Coding daily",
];

const HEADLINE = ["Find", "someone", "who", "|won’t quit|", "before", "you", "do."];

export default function Landing() {
  const reduce = useReducedMotion();
  const [catalog, setCatalog] = useState(null);
  const openCohorts = useOpenCohorts();
  const nextCohort = openCohorts[0] || null;
  useEffect(() => {
    getBillingCatalog().then(setCatalog).catch(() => {});
  }, []);
  const currency = catalog?.currency || "INR";
  const proPack = catalog?.proPlans?.find((plan) => plan.code === "pro_match_pack");
  const winterArc = catalog?.winterArcPlans?.find((plan) => plan.code === "winter_arc_pass");
  const pricing = {
    freePrice: formatMoney(0, currency),
    proPrice: formatMoney(proPack?.amountMinor ?? 39900, currency),
    winterArcPrice: formatMoney(winterArc?.amountMinor ?? 59900, currency),
  };

  const word = (i) => ({
    initial: reduce ? false : { opacity: 0, y: "0.4em", filter: "blur(12px)" },
    animate: { opacity: 1, y: 0, filter: "blur(0px)" },
    transition: { duration: 1.1, delay: 0.15 + i * 0.07, ease: EASE },
  });
  const rise = (delay) => ({
    initial: reduce ? false : { opacity: 0, y: 18 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.9, delay, ease: EASE },
  });

  return (
    <div className="landing relative min-h-screen bg-[#0A0A0A] text-[#F5F5F0] overflow-x-clip">
      <Nav />

      {/* HERO — first light */}
      <section data-testid="hero-section" className="relative min-h-[100svh] flex flex-col justify-end pt-32 pb-16 sm:pb-20 overflow-hidden">
        <div className="absolute inset-0 z-0 grain-overlay" aria-hidden="true">
          <div className="dawn" />
          <div className="dawn-horizon" />
        </div>

        <div className="relative z-10 max-w-6xl mx-auto px-6 sm:px-10 w-full">
          <h1
            data-testid="hero-headline"
            className="font-serif-editorial font-light tracking-[-0.035em] leading-[0.98] text-[3.1rem] sm:text-7xl lg:text-[6rem] max-w-5xl text-balance"
          >
            {HEADLINE.map((w, i) => {
              if (w.startsWith("|")) {
                return (
                  <motion.span key={w} {...word(i)} className="inline-block whitespace-nowrap relative mr-[0.22em]">
                    <em className="italic text-[#F59E0B]">{w.replaceAll("|", "")}</em>
                    <svg viewBox="0 0 300 24" preserveAspectRatio="none" className="absolute left-[-2%] w-[104%] -bottom-[0.08em] h-[0.22em] overflow-visible" aria-hidden="true">
                      <defs>
                        <clipPath id="hero-underline-reveal">
                          <motion.rect
                            x="-10" y="-20" height="64"
                            initial={reduce ? false : { width: 0 }}
                            animate={{ width: 320 }}
                            transition={{ duration: 1.1, delay: 1.05, ease: [0.65, 0, 0.35, 1] }}
                          />
                        </clipPath>
                      </defs>
                      <path
                        d="M3 15 C 50 5, 110 3, 160 9 S 250 21, 297 7"
                        fill="none"
                        stroke="#F59E0B"
                        strokeWidth="3"
                        strokeLinecap="round"
                        vectorEffect="non-scaling-stroke"
                        clipPath="url(#hero-underline-reveal)"
                      />
                    </svg>
                  </motion.span>
                );
              }
              return (
                <motion.span key={w} {...word(i)} className="inline-block mr-[0.22em]">{w}</motion.span>
              );
            })}
          </h1>

          <div className="mt-12 sm:mt-16 grid lg:grid-cols-12 gap-12 lg:gap-10 items-end">
            <motion.div {...rise(0.75)} className="lg:col-span-5">
              <p className="max-w-md text-base sm:text-lg text-[#A1A1AA] leading-relaxed font-light">
                Whether you’re studying, building a startup, getting fit, or mastering a new skill —
                get paired with an accountability partner who is <span className="text-[#F5F5F0]">just as committed</span> as you are.
              </p>
              <div className="mt-9 flex flex-wrap items-center gap-3">
                <Link
                  to="/auth"
                  data-testid="hero-cta-button"
                  className="hm-btn-primary group inline-flex items-center gap-2 px-7 py-4 rounded-md text-sm font-semibold tracking-wide"
                >
                  Find My Partner
                  <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5" strokeWidth={1.75} />
                </Link>
                <a
                  href="#day"
                  data-testid="hero-secondary-link"
                  className="hm-btn-ghost inline-flex items-center gap-2 px-6 py-4 rounded-md text-sm font-medium"
                >
                  See a day, paired
                </a>
              </div>
              <p className="mt-5 text-xs text-[#7c7c84]">Free to start. Includes a full seven-day workspace trial.</p>
              <NextCohortPill cohort={nextCohort} />
            </motion.div>

            <motion.div {...rise(1.0)} className="lg:col-span-6 lg:col-start-7">
              <PairLedger />
            </motion.div>
          </div>
        </div>
      </section>

      {/* GOALS — what people here are chasing */}
      <section className="relative border-y border-white/5 py-7 overflow-hidden marquee-mask" data-testid="goals-marquee" aria-label="Goals people bring to HustlrMate">
        <div className="flex w-max marquee-track whitespace-nowrap">
          {[...goals, ...goals].map((g, i) => (
            <span key={i} className="flex items-center font-serif-editorial italic text-2xl sm:text-3xl text-[#A1A1AA] hover:text-[#F5F5F0] transition-colors duration-500" aria-hidden={i >= goals.length}>
              <span className="mx-8 sm:mx-10">{g}</span>
              <span className="w-1.5 h-1.5 rounded-full bg-[#F59E0B]/70" aria-hidden="true" />
            </span>
          ))}
        </div>
      </section>

      {/* PROBLEM — the ways people ghost */}
      <section id="problem" data-testid="problem-section" className="relative py-28 sm:py-40">
        <div className="max-w-6xl mx-auto px-6 sm:px-10 grid lg:grid-cols-12 gap-14 lg:gap-16 items-start">
          <motion.div className="lg:col-span-5 lg:sticky lg:top-32">
            <h2 className="font-serif-editorial font-light tracking-tight leading-[1.04] text-4xl sm:text-[3.4rem] text-balance">
              Stop trying to stay disciplined <em className="italic">alone.</em>
            </h2>
            <p className="mt-8 text-[#A1A1AA] text-lg font-light leading-relaxed">
              People don’t fail because they lack motivation. They fail because they lack{" "}
              <span className="text-[#F5F5F0]">someone who expects them to show up.</span>
            </p>
            <p className="mt-5 text-[#A1A1AA] font-light leading-relaxed">
              Every serious operator you admire has one thing in common — a partner, a coach, a
              training group. Discipline is a social contract. We just built the platform for it.
            </p>
          </motion.div>
          <div className="lg:col-span-7">
            <GhostFeed />
          </div>
        </div>
      </section>

      {/* SIGNATURE — a day, paired */}
      <PairedDay />

      {/* COMMITMENT LAYER */}
      <section id="features" data-testid="features-section" className="relative py-28 sm:py-40">
        <div className="max-w-6xl mx-auto px-6 sm:px-10">
          <motion.div className="grid lg:grid-cols-12 gap-8 items-end">
            <h2 className="lg:col-span-7 font-serif-editorial font-light tracking-tight leading-[1.02] text-4xl sm:text-6xl">
              Not another app.
              <br />
              <em className="italic text-[#F59E0B]">A commitment layer.</em>
            </h2>
            <p className="lg:col-span-5 text-[#A1A1AA] font-light leading-relaxed max-w-md">
              HustlrMate is engineered around a single question: <span className="text-[#F5F5F0]">who’s
              going to notice if you don’t show up today?</span>
            </p>
          </motion.div>

          <ul className="mt-20 border-t border-white/[0.08]">
            {fixes.map((f, i) => (
              <motion.li
                key={f.title}
                data-testid={`fix-card-${i}`}
                className="fix-row group relative grid md:grid-cols-12 gap-2 md:gap-8 py-7 sm:py-8 border-b border-white/[0.08]"
              >
                <span className="fix-row-rule" aria-hidden="true" />
                <h3 className="md:col-span-5 font-serif-editorial text-[1.75rem] sm:text-[2.1rem] leading-tight transition-transform duration-500 ease-out group-hover:translate-x-2">
                  {f.title}
                </h3>
                <p className="md:col-span-6 md:col-start-7 self-center text-[15px] text-[#A1A1AA] font-light leading-relaxed group-hover:text-[#d4d4d8] transition-colors duration-500">
                  {f.body}
                </p>
              </motion.li>
            ))}
          </ul>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" data-testid="how-section" className="relative py-28 sm:py-36 border-t border-white/5 bg-[#080808]">
        <div className="max-w-6xl mx-auto px-6 sm:px-10">
          <motion.h2 className="font-serif-editorial font-light tracking-tight leading-[1.04] text-4xl sm:text-6xl max-w-3xl">
            Three quiet steps.
            <br />
            <em className="italic">One loud year.</em>
          </motion.h2>

          <ol className="mt-20 grid md:grid-cols-3 gap-12 md:gap-10">
            {steps.map((s, i) => (
              <motion.li
                key={s.n}
                data-testid={`step-${i}`}
                initial={reduce ? false : { opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-15% 0px" }}
                transition={{ duration: 0.9, delay: i * 0.14, ease: EASE }}
                className="relative pt-8"
              >
                <motion.span
                  aria-hidden="true"
                  className="absolute top-0 left-0 right-0 h-px bg-[#F59E0B] origin-left"
                  initial={reduce ? false : { scaleX: 0 }}
                  whileInView={{ scaleX: 1 }}
                  viewport={{ once: true, margin: "-15% 0px" }}
                  transition={{ duration: 1.2, delay: 0.2 + i * 0.25, ease: EASE }}
                />
                <span className="absolute top-0 left-0 right-0 h-px bg-white/10" aria-hidden="true" />
                <span className="block font-serif-editorial italic text-6xl sm:text-7xl leading-none text-[#F59E0B]/90">{s.n}</span>
                <h3 className="mt-8 font-serif-editorial text-3xl leading-tight">{s.title}</h3>
                <p className="mt-3 text-[15px] text-[#A1A1AA] font-light leading-relaxed max-w-xs">{s.body}</p>
              </motion.li>
            ))}
          </ol>
        </div>
      </section>

      <CohortSection cohorts={openCohorts} />

      {/* FILTER — why a paid tier exists, right before the prices */}
      <section data-testid="paywall-section" className="relative py-24 sm:py-32 border-t border-white/5">
        <div className="max-w-6xl mx-auto px-6 sm:px-10">
          <motion.figure className="grid lg:grid-cols-12 gap-8 items-end">
            <blockquote className="lg:col-span-8 font-serif-editorial font-light text-4xl sm:text-[3.6rem] leading-[1.04] tracking-tight text-balance">
              A paywall is a filter. <span className="italic text-[#F59E0B]">It keeps the tourists out.</span>
            </blockquote>
            <figcaption className="lg:col-span-4 text-[#A1A1AA] font-light leading-relaxed">
              Free platforms attract browsers. Paid ones attract builders. That’s not gatekeeping —
              that’s calibration.
            </figcaption>
          </motion.figure>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" data-testid="pricing-section" className="relative py-28 sm:py-40 border-t border-white/5">
        <div className="relative max-w-6xl mx-auto px-6 sm:px-10">
          <motion.div className="grid lg:grid-cols-12 gap-8 items-end mb-16">
            <h2 className="lg:col-span-7 font-serif-editorial font-light tracking-tight leading-[1.04] text-4xl sm:text-6xl">
              Don’t overcomplicate.
            </h2>
            <p className="lg:col-span-5 text-[#A1A1AA] font-light max-w-md">
              Start free, go Pro, or enter the limited Winter Arc with a stricter daily standard.
            </p>
          </motion.div>

          <div className={`grid gap-6 ${catalog?.features?.winterArc ? "lg:grid-cols-3" : "md:grid-cols-2"}`}>
            {/* FREE */}
            <SpotCard data-testid="pricing-free" className="hm-card p-8 sm:p-10 rounded-lg flex flex-col">
              <div className="text-sm text-[#F5F5F0] font-medium">Free</div>
              <div className="mt-6 flex items-baseline gap-2 mb-2">
                <span className="font-serif-editorial text-6xl tabular-nums">{pricing.freePrice}</span>
                <span className="text-[#A1A1AA] text-sm">/ forever</span>
              </div>
              <p className="text-sm text-[#A1A1AA] mb-8">Includes a full seven-day workspace trial.</p>
              <ul className="space-y-3 mb-10 text-sm">
                {["Browse profiles", "Limited likes", "One active match", "Limited chat length", "Weekly check-ins", "Ads"].map((f) => (
                  <li key={f} className="flex items-start gap-3">
                    <Check className="w-4 h-4 mt-0.5 text-[#A1A1AA]" strokeWidth={2} />
                    <span className="text-[#A1A1AA] font-light">{f}</span>
                  </li>
                ))}
              </ul>
              <Link
                to="/auth"
                data-testid="pricing-free-cta"
                className="hm-btn-ghost inline-flex justify-center items-center px-6 py-3 rounded-md text-sm font-medium mt-auto"
              >
                Start free
              </Link>
            </SpotCard>

            {/* PRO */}
            <SpotCard data-testid="pricing-pro" className="pro-card relative rounded-lg p-8 sm:p-10 flex flex-col">
              <div className="flex items-center justify-between mb-0">
                <span className="text-sm text-[#F59E0B] font-medium">Pro</span>
                <span className="text-[10px] uppercase tracking-[0.2em] px-2 py-1 rounded-sm bg-[#F59E0B] text-black font-semibold">
                  Most users
                </span>
              </div>
              <div className="mt-6 flex items-baseline gap-2 mb-2">
                <span className="font-serif-editorial text-6xl tabular-nums">{pricing.proPrice}</span>
                <span className="text-[#A1A1AA] text-sm">/ 4 new matches</span>
              </div>
              <p className="text-sm text-[#A1A1AA] mb-8">No time expiry. Each purchase unlocks four new matches and the Pro experience.</p>
              <ul className="space-y-3 mb-10 text-sm">
                {[
                  "4 new matches per purchase",
                  "Convert matches into partnerships",
                  "Up to 3 active partnerships",
                  "Priority visibility",
                  "Unlimited chat length",
                  "No ads",
                  "Habit tracking",
                  "Voice / video session tracking",
                  "Advanced filters",
                  "Read receipts",
                  "Streaks & goal analytics",
                ].map((f) => (
                  <li key={f} className="flex items-start gap-3">
                    <Check className="w-4 h-4 mt-0.5 text-[#F59E0B]" strokeWidth={2} />
                    <span className="text-[#F5F5F0] font-light">{f}</span>
                  </li>
                ))}
              </ul>
              <Link
                to="/auth"
                data-testid="pricing-pro-cta"
                className="hm-btn-primary group inline-flex justify-center items-center gap-2 px-6 py-3 rounded-md text-sm font-semibold mt-auto"
              >
                Get Pro
                <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5" strokeWidth={1.75} />
              </Link>
            </SpotCard>

            {catalog?.features?.winterArc && (
              <SpotCard data-testid="pricing-winter-arc" spot="255,255,255" className="relative overflow-hidden border-2 border-white bg-black p-8 sm:p-10 flex flex-col min-h-[620px]">
                <Snowflake className="absolute -right-10 -top-10 w-44 h-44 text-white/[.055]" strokeWidth={0.6} />
                <div className="relative flex items-center justify-between gap-3">
                  <span className="text-[10px] uppercase tracking-[.28em] text-white">Winter Arc</span>
                  <span className="text-[9px] uppercase tracking-[.2em] border border-white/30 px-2 py-1 text-white/70">Seasonal</span>
                </div>
                <div className="relative mt-6 flex items-baseline gap-2 mb-2">
                  <span className="font-serif-editorial text-6xl text-white tabular-nums">{pricing.winterArcPrice}</span>
                </div>
                <p className="relative text-sm text-white/50 mb-8">Active until January 1, 2027. Includes four matches, Pro access, and the full Winter Arc discipline system. Winter Arc remains active if those matches are used.</p>
                <ul className="relative space-y-3 mb-10 text-sm">
                  {["Everything in Pro", "Minimal black & white member theme", "Nine daily non-negotiables", "Link existing tasks or create the defaults", "Daily 0–100 execution score", "30-day score history and perfect-day streak", "Winter Arc profile signal"].map((f) => (
                    <li key={f} className="flex items-start gap-3"><Check className="w-4 h-4 mt-0.5 text-white" strokeWidth={2} /><span className="text-white/80 font-light">{f}</span></li>
                  ))}
                </ul>
                <Link to="/auth?plan=winter-arc" data-testid="pricing-winter-arc-cta" className="relative mt-auto inline-flex justify-center items-center gap-2 px-6 py-3 bg-white text-black text-sm font-semibold hover:bg-neutral-200 transition-colors">
                  Enter Winter Arc <ArrowRight className="w-4 h-4" strokeWidth={1.75} />
                </Link>
              </SpotCard>
            )}
          </div>
        </div>
      </section>

      {/* FINAL — the pact */}
      <section data-testid="final-cta" className="relative py-28 sm:py-40 border-t border-white/5 overflow-hidden">
        <div className="absolute inset-0 grain-overlay" aria-hidden="true"><div className="dawn dawn--low" /></div>
        <div className="relative max-w-6xl mx-auto px-6 sm:px-10">
          <motion.h2 className="text-center font-serif-editorial font-light tracking-tight leading-[1.02] text-4xl sm:text-6xl lg:text-[4.6rem] text-balance">
            One year from now, you’ll wish
            <br className="hidden sm:block" />{" "}
            <em className="italic text-[#F59E0B]">you started today.</em>
          </motion.h2>
          <motion.p className="mt-6 mb-14 text-center text-[#A1A1AA] font-light max-w-xl mx-auto">
            Skip the Reddit threads. Skip the Discord servers. Get paired with someone who’s already
            committed to showing up — and helping you do the same.
          </motion.p>
          <motion.div>
            <Pact />
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

// Card with a soft light that follows the pointer.
function SpotCard({ children, className = "", spot = "245,158,11", ...rest }) {
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty("--mx", `${e.clientX - r.left}px`);
    e.currentTarget.style.setProperty("--my", `${e.clientY - r.top}px`);
  };
  return (
    <div {...rest} onPointerMove={onMove} className={`spot-card ${className}`} style={{ "--spot": spot }}>
      {children}
    </div>
  );
}
